import React, { FunctionComponent } from 'react'
import { observer } from 'mobx-react-lite'
import styled from 'styled-components'
import { CredentialResponse, GoogleLogin } from '@react-oauth/google'
import Link from '../components/Link'
import SimpleForm from '../components/SimpleForm'
import { AuthenticationView, ErrorMessage, ComponentView } from '../components/StyledComponents'
import Text, { BoldText } from '../components/Text'
import { colors, spacing } from '../styles/_var'
import { loginFields, routes } from '../utils/constants'
import authStore from '../stores/authStore'
import { IUserCredentials } from '../utils/interfaces'
import { parseUsername } from '../utils/helpers'
import useRenderOnNotAuthenticated from '../hooks/useRenderOnNotAuthenticated'
import usePageTitle from '../hooks/usePageTitle'
import { useNavigate } from 'react-router-dom'
import { useSubdomain } from '../hooks/useSubdomain'


interface ILoginProps {
  className?: string
}

const Login: FunctionComponent<ILoginProps> = ({ className }) => {
  usePageTitle('Log In')
  const navigate = useNavigate()
  const subdomain = useSubdomain()
  const shouldLoadPage = useRenderOnNotAuthenticated()
  if (!shouldLoadPage) return null

  const logUserIn = async (data: IUserCredentials) => {
    await authStore.loginUser({
      ...data,
      username: parseUsername(data.username)
    }, () => {
      navigate(getRedirectUrl())
    })
  }

  const getRedirectUrl = () => {
    return !!subdomain ? '/' : routes.DASHBOARD
  }

  const handleLogin = (data: CredentialResponse) => {
    const { credential } = data

    if (credential) {
      authStore.googleAuth(credential, () => {
        navigate(getRedirectUrl())
      })
    }
  }

  const handleError = () => {}

  return (
    <AuthenticationView>
      <ComponentView className={className}>
        <TitleText variant="medium" role="heading">Log In</TitleText>
        <SimpleForm 
          formFields={loginFields}
          submitForm={logUserIn}
          actionButtons={{
            label: 'Login Now',
            loading: authStore.loginUserLoading,
            invertOnHover: true,
            isSubmit: true
          }}
        />
        <LoginErrorMessage>{authStore.loginUserError}</LoginErrorMessage>
        <BoldText variant='regular' center><em>Or</em></BoldText>
        <GoogleButtonContainer>
          <GoogleLogin 
            text="continue_with"
            onSuccess={handleLogin}
            onError={handleError} />
        </GoogleButtonContainer>
        <FooterText variant="small">Don't have an account? 
          <Link to={routes.SIGNUP}>Register Here!</Link>
        </FooterText>
        <ForgotPasswordText variant="small">
          <Link to={routes.FORGOT_PASSWORD}>Forgot Password?</Link>
        </ForgotPasswordText>
      </ComponentView>
    </AuthenticationView>
  )
}

export default observer(Login)


const LoginErrorMessage = styled(({ ...rest }) => <ErrorMessage {...rest} />)`
  position: relative;
`

export const GoogleButtonContainer = styled.div`
  margin-right: auto;
  margin-left: auto;
  width: 30rem;
  color: ${colors.black} !important;
  cursor: pointer;

  > div {
    width: 100%;
  }
`
  
const FooterText = styled(({ ...rest }) => <Text {...rest} />)`
  margin-top: ${spacing.sm};
  text-align: center;

  & a {
    text-decoration: none;
    margin-left: 0.5rem;
  }
`

const TitleText = styled(({ ...rest }) => <Text {...rest} />)`
  margin-bottom: ${spacing.xs};
`

const ForgotPasswordText = styled(({ ...rest }) => <Text {...rest} />)`
  margin-top: 0;
  position: absolute;
  right: 3rem;
  top: 23rem;
  
  a {
    text-decoration: none;
  }
`